// ABOUTME: Account view where a user picks which environment opens by default after login.
// ABOUTME: Persists the choice on the user record and saves instantly on change.

import { Box } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Text from "../../../../components/theme/text/Text";
import { InputDropdown } from "../../../../components/theme/dropdowns/InputDropdown";
import { useEditUserMutation } from "../../../../newapi/user/userSlice";
import {
  selectUser,
  selectCustomers,
  selectRelations,
  selectDefaultEnvironment,
  setDefaultEnvironment,
} from "../../../../newapi/user/authentication/authSlice";
import { ENV_TYPES } from "../../../../components/global/Sidebar";
import { showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../utils/toasts";

const MainEnvironment = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const customers = useSelector(selectCustomers);
  const relations = useSelector(selectRelations);
  const defaultEnvironment = useSelector(selectDefaultEnvironment);

  const [updateUser] = useEditUserMutation();

  useEffect(() => {
    props.replaceView({
      ...props.config,
    });
  }, []);

  // Only permanent environments are selectable; temporary ones are transient.
  // Keyed on type + id instead of list position: saving a main environment hoists it
  // to the front of its group in the sidebar rail, so an index would point at a
  // different environment after every save. Relations key on internalId (the stable
  // root id); login rewrites relation.id to the per-customer externalId.
  // The dropdown maps an option to its value through valueKey and then compares values.
  // Pointing each option's value at the option itself turns that into an identity check,
  // which the input field and the open list both agree on.
  const options = useMemo(() => {
    const opts = [];
    (customers ?? [])
      .filter((customer) => !customer.temp)
      .forEach((customer) => {
        const option = { name: customer.name, type: ENV_TYPES[100], envId: customer.id };
        option.value = option;
        opts.push(option);
      });
    (relations ?? [])
      .filter((relation) => !relation.temp)
      .forEach((relation) => {
        const option = { name: relation.name, type: ENV_TYPES[200], envId: relation.internalId };
        option.value = option;
        opts.push(option);
      });
    return opts;
  }, [customers, relations]);

  // Resolve the stored preference against the options rather than rebuilding its key, so a
  // type or id that does not line up falls back to the placeholder instead of a blank
  // input. -1 is what InputDropdown reads as "nothing selected".
  const selectedValue = useMemo(() => {
    if (!defaultEnvironment) return -1;
    const match = options.find((option) => option.type === defaultEnvironment.type && option.envId === defaultEnvironment.id);
    return match ?? -1;
  }, [options, defaultEnvironment]);

  const [pendingValue, setPendingValue] = useState(selectedValue);

  // Keep the dropdown in sync if the stored preference changes elsewhere.
  useEffect(() => {
    setPendingValue(selectedValue);
  }, [selectedValue]);

  // Settings pages persist on change rather than behind a save button.
  // A null value means the selection was toggled off (no main environment).
  const handleChange = async (value) => {
    const previousValue = pendingValue;
    let nextValue = value;
    if (!nextValue) {
      nextValue = -1;
    }
    setPendingValue(nextValue);

    const type = value?.type ?? null;
    const envId = value?.envId ?? null;

    try {
      const response = await updateUser({
        body: {
          id: user.id,
          body: {
            id: user.id,
            default_environment_type: type,
            default_environment_id: envId,
          },
        },
      }).unwrap();
      showToastMessageUpdateRequest(t, response);
      dispatch(setDefaultEnvironment(type ? { type, id: envId } : null));
    } catch (e) {
      setPendingValue(previousValue);
      showToastMessageRequestError(t("update_failed"), t, e);
    }
  };

  return (
    <Box
      sx={{
        pt: 4,
        height: 1,
        "@media screen and (max-width: 47.9375em)": {
          paddingTop: 0,
          paddingX: 0,
        },
      }}
    >
      <Box
        sx={{
          borderRadius: 4,
          px: 4,
          py: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          "@media screen and (max-width: 47.9375em)": {
            mx: 2,
          },
        }}
      >
        <Box
          sx={{
            width: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 4,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Text semibold>{t("main_environment_title")}</Text>
            <Text light>{t("main_environment_subtitle")}</Text>
          </Box>
          <Box
            sx={{
              flex: 1,
              maxWidth: 250,
              borderRadius: 4,
              display: "flex",
              height: 32,
            }}
          >
            <InputDropdown
              bold
              variant={"default"}
              displayName={"name"}
              valueKey={"value"}
              options={options}
              selected={pendingValue}
              noSelection={t("select_environment")}
              onChange={handleChange}
              closeOnSelection
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MainEnvironment;
